import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Clock, MailCheck, Copy, Inbox, BellRing, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tone = (days) => {
  if (days === null || days === undefined) return "bg-linen text-ink-500";
  if (days >= 7) return "bg-red-50 text-red-700";
  if (days >= 3) return "bg-amber-50 text-amber-700";
  return "bg-teal-light text-teal";
};

export const ApplicantPipelineCard = ({ endpoint, authHeader, scoped = false }) => {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState({ enabled: false, officer_alerts: false, threshold_days: 3 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}${endpoint}`, { headers: authHeader() })
      .then((r) => {
        setData(r.data);
        if (r.data.alerts) setAlerts(r.data.alerts);
      })
      .catch(() => setData({ rows: [], waiting: 0, stale: 0 }));
  }, [endpoint, authHeader]);

  const saveAlerts = async () => {
    setBusy(true);
    try {
      const { data: saved } = await axios.put(
        `${API}/admin/pipeline-alerts`,
        { ...alerts, threshold_days: Number(alerts.threshold_days) || 3 },
        { headers: authHeader() },
      );
      setAlerts(saved);
      setData((d) => ({ ...d, alerts: saved }));
      toast.success(saved.enabled ? "Daily alerts on" : "Alert settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that.");
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(
        `${API}/admin/pipeline-alerts/run?officers=${alerts.officer_alerts ? "true" : "false"}`,
        {},
        { headers: authHeader() },
      );
      toast.success(
        res.stale
          ? `Digest sent — ${res.stale} waiting${res.officer_sends?.length ? `, ${res.officer_sends.length} officer(s) nudged` : ""}`
          : "Nothing is over the threshold, so no digest was sent",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send that.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (row) => {
    if (!row.status_link) return;
    const ok = await copyText(row.status_link);
    toast[ok ? "success" : "error"](ok ? "Status link copied" : "Couldn't copy — select the link manually.");
  };

  if (!data) {
    return (
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6 flex items-center gap-2 text-sm text-ink-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the pipeline…
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6" data-testid="applicant-pipeline-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-teal font-bold">
            <Inbox size={13} /> Waiting on you
          </div>
          <h3 className="mt-1 font-heading font-medium text-xl text-navy">
            {data.waiting === 0
              ? "Nobody's waiting."
              : `${data.waiting} applicant${data.waiting === 1 ? "" : "s"} waiting on a decision`}
          </h3>
          <p className="mt-1 text-sm text-ink-500">
            {scoped
              ? "People who applied through your own partner link and haven't been reviewed yet."
              : "Every partner application still sitting in New or Under Review, oldest first."}
          </p>
        </div>
        {data.stale > 0 && (
          <span
            data-testid="pipeline-stale-count"
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3.5 py-1.5 text-xs font-bold"
          >
            <Clock size={13} /> {data.stale} over {data.threshold_days ?? 3} days
          </span>
        )}
      </div>

      {data.rows.length === 0 ? (
        <p className="mt-5 rounded-xl bg-linen/60 border border-linen-border p-4 text-sm text-ink-500">
          {scoped
            ? "Nothing in your queue. When someone applies through your link, they'll show up here with a running clock."
            : "The queue is clear — every application has a decision on it."}
        </p>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="pipeline-table">
            <thead>
              <tr className="text-left text-ink-500 border-b border-linen-border">
                <th className="py-2 pr-3 font-medium">Applicant</th>
                <th className="py-2 pr-3 font-medium">Waiting</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                {!scoped && <th className="py-2 pr-3 font-medium">Officer</th>}
                <th className="py-2 pr-3 font-medium">Follow-up</th>
                <th className="py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-linen-border/60" data-testid={`pipeline-row-${r.id}`}>
                  <td className="py-2.5 pr-3">
                    <span className="font-medium text-navy">{r.full_name || r.email}</span>
                    <div className="text-[11px] text-ink-400">
                      {r.email}
                      {r.company ? ` · ${r.company}` : ""}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <span
                      data-testid={`pipeline-days-${r.id}`}
                      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold ${tone(r.days_waiting)}`}
                    >
                      {r.days_waiting === null ? "—" : `${r.days_waiting}d`}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-ink-500 text-xs capitalize">
                    {(r.status || "").replace("_", " ")}
                  </td>
                  {!scoped && (
                    <td className="py-2.5 pr-3 text-xs text-ink-500">
                      {r.referring_mlo_slug ? `/${r.referring_mlo_slug}` : <span className="text-amber-700">Unassigned</span>}
                    </td>
                  )}
                  <td className="py-2.5 pr-3">
                    {r.followup_sent_at ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-teal">
                        <MailCheck size={12} /> Sent
                      </span>
                    ) : (
                      <span className="text-[11px] text-ink-400">—</span>
                    )}
                  </td>
                  <td className="py-2.5 text-right">
                    <button
                      onClick={() => copy(r)}
                      disabled={!r.status_link}
                      data-testid={`pipeline-copy-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-linen hover:bg-linen-border text-navy disabled:opacity-40"
                    >
                      <Copy size={12} /> Status link
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!scoped && data.alerts && (
        <div className="mt-5 rounded-2xl bg-linen/60 border border-linen-border p-4" data-testid="pipeline-alerts-settings">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-teal font-bold">
            <BellRing size={13} /> Daily alerts
          </div>
          <label className="mt-3 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(alerts.enabled)}
              onChange={(e) => setAlerts((a) => ({ ...a, enabled: e.target.checked }))}
              data-testid="pipeline-alerts-enabled"
              className="mt-0.5 w-4 h-4 accent-teal"
            />
            <span>
              <span className="text-sm font-semibold text-navy">Email me a digest of anyone waiting too long</span>
              <span className="block text-[12px] text-ink-500 leading-relaxed">
                Once a day, only when there's someone to report. Goes to {data.alerts.admin_email || "the admin address"}.
              </span>
            </span>
          </label>
          <label className="mt-3 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(alerts.officer_alerts)}
              onChange={(e) => setAlerts((a) => ({ ...a, officer_alerts: e.target.checked }))}
              data-testid="pipeline-alerts-officers"
              className="mt-0.5 w-4 h-4 accent-teal"
            />
            <span>
              <span className="text-sm font-semibold text-navy">Also nudge the referring officer</span>
              <span className="block text-[12px] text-ink-500 leading-relaxed">
                Each officer hears only about the applicants who came through their own link.
              </span>
            </span>
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-3 pl-7">
            <label className="flex items-center gap-2 text-xs text-ink-600">
              After
              <input
                type="number"
                min={1}
                max={30}
                value={alerts.threshold_days ?? 3}
                onChange={(e) => setAlerts((a) => ({ ...a, threshold_days: e.target.value }))}
                data-testid="pipeline-alerts-threshold"
                className="w-16 rounded-lg border border-linen-border p-1.5 text-sm text-navy bg-white text-center"
              />
              days
            </label>
            <button
              onClick={saveAlerts}
              disabled={busy}
              data-testid="pipeline-alerts-save"
              className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white font-semibold text-xs px-4 py-2 rounded-full transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
            <button
              onClick={sendNow}
              disabled={busy}
              data-testid="pipeline-alerts-send-now"
              className="inline-flex items-center gap-1.5 bg-white border border-linen-border hover:border-teal text-navy font-semibold text-xs px-4 py-2 rounded-full transition-colors disabled:opacity-60"
            >
              <Send size={13} /> Send digest now
            </button>
            {data.alerts.last_sent_count ? (
              <span className="text-[11px] text-ink-400">Last digest covered {data.alerts.last_sent_count}</span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicantPipelineCard;
